import { ModalFooter, useModalControls } from "@hostingbv/lib/src/components/layout/ModalProvider";
import { FC, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useCertificates, useUpdateWindhostingSite, useWindhostingSite } from "@hostingbv/lib/src/api/partner/PartnerHooks";
import { ValidationError } from "@hostingbv/lib/src/api/ModelFactory";
import { useRealtimeDeployments } from "../components/deployments/RealtimeDeployments";
import { Site } from "@hostingbv/lib/src/api/partner/models/Site";
import { SiteConfig } from "@hostingbv/lib/src/api/partner/models/windhosting/SiteConfig";
import { Autocomplete } from "@hostingbv/lib/src/components/form/Autocomplete";
import { TabLayout } from "@hostingbv/lib/src/components/content/TabLayout";

export const WindhostingSiteTLSConfigModal: FC<{ site: Site }> = ({ site }) => {
  const [certificate, setCertificate] = useState("")
  const [deploymentError, setDeploymentError] = useState("")

  const { data: windhostingSite } = useWindhostingSite(site.serverId, site.id)
  useEffect(() => setCertificate(windhostingSite?.certificate ?? ""), [windhostingSite])

  const { data: certificates } = useCertificates()
  const certificateOptions = useMemo(() => Object.fromEntries(certificates?.filter(x => x.windhostingServer === site.serverId).map(x => [x.displayName, x.displayName]) ?? []), [certificates])

  const { mutate, error } = useUpdateWindhostingSite(site.serverId, site.id)
  const { awaitNextDeploymentFinished } = useRealtimeDeployments()!
  const save = async () => {
    if (!windhostingSite) return
    const deployment = await mutate(new SiteConfig(certificate, windhostingSite.phpVersion, windhostingSite.phpPoolConfig, windhostingSite.webserverConfig, windhostingSite.aliasDomains));

    const updatedDeployment = await awaitNextDeploymentFinished(deployment.id)
    if (updatedDeployment?.status === "failed") {
      // @ts-expect-error output is untyped
      setDeploymentError(updatedDeployment?.output?.message ?? 'Invalid configuration')
      throw Error("deployment failed");
    }
  }

  const navigate = useNavigate()
  const { close } = useModalControls()
  const addCertificate = () => {
    const name = encodeURIComponent(site.domain ?? '')
    const allDomains = [site.domain, ...site.aliasDomains]
    const domains = encodeURIComponent([...new Set(allDomains)].join(','))
    navigate(`/certificates/add?name=${name}&domain=${domains}`)
    close()
  }

  const [certificateTab, setCertificateTab] = useState("Use existing")
  const selectCertificateTab = (tab: string) => tab === "Create new" ? addCertificate() : setCertificateTab(tab)

  return <>
    <form onSubmit={() => save()} className={""}>
      <div>
        <TabLayout tabNames={["Use existing", "Create new"]} activeTab={certificateTab} setActiveTab={selectCertificateTab} tabContents={[
          <Autocomplete label={"TLS certificate"} value={certificate} onChange={setCertificate} options={certificateOptions} />,
          <></>
        ]} />
        {error instanceof ValidationError && <div className={"px-3 py-2 bg-red-50 rounded-sm my-4 text-sm text-red-900"}>
          {error.message}
        </div>}
        {deploymentError !== '' && <div className={"px-3 py-2 bg-red-50 rounded-sm my-4 text-sm text-red-900"}>
          {deploymentError}
        </div>}
      </div>
      <ModalFooter text={"Save"} onSubmit={save} />
    </form>
  </>
}
